import { useState, useEffect } from 'react'
import { Button, Loading, Tag } from 'react-vant'
import {
    Records, Search, Description, Success, Play, CalendarO,
    BullhornO, Exchange, ManagerO, BalanceListO, Arrow, ShieldO,
    MedalO, ChartTrendingO
} from '@react-vant/icons'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import request from '../../api/request'
import { canManagePhysicalTest } from '../../utils/permissions'

export default function SportsHome() {
    const navigate = useNavigate()
    const { userInfo } = useSelector(state => state.user)
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState(0) // 0: 体质健康管理, 1: 运动会与班级赛
    const [stats, setStats] = useState({
        totalStudents: 0,
        testedStudents: 0,
        completionRate: 0,
        goodRate: 0,
        excellentRate: 0
    })

    useEffect(() => {
        const fetchSportsData = async () => {
            setLoading(true)
            try {
                const yearRes = await request.get('/academic-year/current/')
                const currentYear = yearRes.current_academic_year || '2025-2026'

                const statsRes = await request.get('/sports/tests/statistics/', {
                    params: { academic_year: currentYear, include_progress: true, include_charts: false }
                })

                if (statsRes.success && statsRes.data?.summary) {
                    const s = statsRes.data.summary
                    const total = s.totalStudents || 0
                    const tested = s.testedStudents || 0
                    setStats({
                        totalStudents: total,
                        testedStudents: tested,
                        completionRate: total > 0 ? Math.round((tested / total) * 100) : 0,
                        goodRate: s.goodRate || 0,
                        excellentRate: s.excellentRate || 0
                    })
                }
            } catch (e) {
                console.error('获取体育数据失败:', e)
            } finally {
                setLoading(false)
            }
        }
        fetchSportsData()
    }, [])

    const isPETeacher = canManagePhysicalTest(userInfo)
    const goodExcellentRate = ((stats.goodRate || 0) + (stats.excellentRate || 0)).toFixed(2)

    return (
        <div style={{ background: 'var(--canvas-bg)', minHeight: '100vh', paddingBottom: 88 }}>
            {/* 1. 沉浸式星瀚空天大页眉 (对标 App 端 colors.brand.gradient) */}
            <div style={{
                background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)',
                padding: '44px 20px 24px',
                color: '#fff',
                position: 'relative'
            }}>
                <h2 style={{ margin: 0, fontSize: 26, fontWeight: 800, letterSpacing: '-0.3px' }}>校园体育</h2>
                <p style={{ margin: '6px 0 0', opacity: 0.88, fontSize: 13, fontWeight: 500 }}>
                    体质健康监测与赛事竞技中心
                </p>
            </div>

            {/* 2. 分段切换栏 (Segmented Tab Bar) */}
            <div style={{ padding: '0 16px', marginTop: '-14px', position: 'relative', zIndex: 20 }}>
                <div style={{
                    display: 'flex',
                    background: 'rgba(255, 255, 255, 0.92)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: 14,
                    padding: 3,
                    boxShadow: '0 4px 16px rgba(15, 23, 42, 0.06)',
                    border: '1px solid #e2e8f0'
                }}>
                    <div
                        onClick={() => setActiveTab(0)}
                        style={{
                            flex: 1,
                            textAlign: 'center',
                            padding: '10px 0',
                            borderRadius: 11,
                            fontSize: 14,
                            fontWeight: activeTab === 0 ? 700 : 500,
                            color: activeTab === 0 ? '#056b4f' : '#64748b',
                            background: activeTab === 0 ? '#ffffff' : 'transparent',
                            boxShadow: activeTab === 0 ? '0 2px 8px rgba(15, 23, 42, 0.08)' : 'none',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6
                        }}
                    >
                        <span>🏃</span> 体质健康管理
                    </div>
                    <div
                        onClick={() => setActiveTab(1)}
                        style={{
                            flex: 1,
                            textAlign: 'center',
                            padding: '10px 0',
                            borderRadius: 11,
                            fontSize: 14,
                            fontWeight: activeTab === 1 ? 700 : 500,
                            color: activeTab === 1 ? '#ff5e1e' : '#64748b',
                            background: activeTab === 1 ? '#ffffff' : 'transparent',
                            boxShadow: activeTab === 1 ? '0 2px 8px rgba(15, 23, 42, 0.08)' : 'none',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6
                        }}
                    >
                        <span>🏆</span> 运动会与班级赛
                    </div>
                </div>
            </div>

            {/* 3. 页面主体内容 */}
            {activeTab === 0 ? (
                /* ========== 轨道 1：体质健康管理 (教师工作台) ========== */
                <div style={{ marginTop: 14 }}>
                    {/* 顶部学生体质状态概览条 (点击下钻至成绩查询) */}
                    <div
                        onClick={() => navigate('/physical-test/analytics')}
                        style={{
                            margin: '0 16px 16px',
                            background: '#ffffff',
                            borderRadius: 16,
                            padding: '14px 18px',
                            boxShadow: 'var(--shadow-card)',
                            border: '1px solid var(--border-subtle)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer'
                        }}
                    >
                        <div style={{ textAlign: 'center', flex: 1 }}>
                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>关联学生</div>
                            <div style={{ fontSize: 17, fontWeight: 800, color: '#056b4f' }}>
                                {loading ? '...' : `${stats.totalStudents} 人`}
                            </div>
                        </div>
                        <div style={{ width: 1, height: 26, background: '#e2e8f0' }} />
                        <div style={{ textAlign: 'center', flex: 1 }}>
                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>体测已完成</div>
                            <div style={{ fontSize: 17, fontWeight: 800, color: '#00b578' }}>
                                {loading ? '...' : `${stats.completionRate}%`}
                            </div>
                        </div>
                        <div style={{ width: 1, height: 26, background: '#e2e8f0' }} />
                        <div style={{ textAlign: 'center', flex: 1 }}>
                            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>优良率</div>
                            <div style={{ fontSize: 17, fontWeight: 800, color: '#7c3aed' }}>
                                {loading ? '...' : `${goodExcellentRate}%`}
                            </div>
                        </div>
                        <Arrow style={{ color: '#94a3b8', fontSize: 14, marginLeft: 6 }} />
                    </div>

                    {/* 模块标头：核心录入通道 */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 16px 10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 4, height: 16, background: '#056b4f', borderRadius: 2 }} />
                            <span style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>核心录入通道</span>
                            <span style={{ fontSize: 12, color: '#64748b' }}>高频测试·专用小键盘</span>
                        </div>
                        <span style={{ fontSize: 12, color: '#056b4f', fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('/physical-test/standards')}>
                            2014版标准 ›
                        </span>
                    </div>

                    {/* 2x2 对称美观大卡网格 (对标 App 端 ActionCard 风格) */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: 12,
                        padding: '0 16px',
                        marginBottom: 18
                    }}>
                        {/* 批量录入 */}
                        <div
                            onClick={() => navigate('/physical-test/bulk-record')}
                            style={{
                                background: '#ffffff',
                                borderRadius: 16,
                                padding: '16px 14px',
                                border: '1px solid #f1f5f9',
                                boxShadow: 'var(--shadow-card)',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'transform 0.15s ease'
                            }}
                        >
                            <span style={{
                                position: 'absolute', top: 10, right: 10,
                                background: '#ff5e1e', color: '#fff',
                                fontSize: 10, fontWeight: 700,
                                padding: '2px 6px', borderRadius: 999
                            }}>极速首选</span>
                            <div style={{
                                width: 44, height: 44, borderRadius: 12,
                                background: '#f0fdf4',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                marginBottom: 12
                            }}>
                                <Records style={{ fontSize: 24, color: '#056b4f' }} />
                            </div>
                            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>批量录入</div>
                            <div style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                全班极速遍历·数字小键盘
                            </div>
                        </div>

                        {/* 单人速测 */}
                        <div
                            onClick={() => navigate('/physical-test/record')}
                            style={{
                                background: '#ffffff',
                                borderRadius: 16,
                                padding: '16px 14px',
                                border: '1px solid #f1f5f9',
                                boxShadow: 'var(--shadow-card)',
                                position: 'relative',
                                cursor: 'pointer'
                            }}
                        >
                            <div style={{
                                width: 44, height: 44, borderRadius: 12,
                                background: '#f0fdf4',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                marginBottom: 12
                            }}>
                                <Success style={{ fontSize: 24, color: '#056b4f' }} />
                            </div>
                            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>单人速测</div>
                            <div style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                精准单兵补测·成长档案
                            </div>
                        </div>

                        {/* 成绩大盘 */}
                        <div
                            onClick={() => navigate('/physical-test/analytics')}
                            style={{
                                background: '#ffffff',
                                borderRadius: 16,
                                padding: '16px 14px',
                                border: '1px solid #f1f5f9',
                                boxShadow: 'var(--shadow-card)',
                                position: 'relative',
                                cursor: 'pointer'
                            }}
                        >
                            <span style={{
                                position: 'absolute', top: 10, right: 10,
                                background: '#056b4f', color: '#fff',
                                fontSize: 10, fontWeight: 700,
                                padding: '2px 6px', borderRadius: 999
                            }}>新</span>
                            <div style={{
                                width: 44, height: 44, borderRadius: 12,
                                background: '#f0fdf4',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                marginBottom: 12
                            }}>
                                <ChartTrendingO style={{ fontSize: 24, color: '#056b4f' }} />
                            </div>
                            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>成绩大盘</div>
                            <div style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                多维统计·成绩大盘·班级下钻
                            </div>
                        </div>

                        {/* 成绩导出 */}
                        <div
                            onClick={() => navigate('/physical-test/export')}
                            style={{
                                background: '#ffffff',
                                borderRadius: 16,
                                padding: '16px 14px',
                                border: '1px solid #f1f5f9',
                                boxShadow: 'var(--shadow-card)',
                                position: 'relative',
                                cursor: 'pointer'
                            }}
                        >
                            <span style={{
                                position: 'absolute', top: 10, right: 10,
                                background: '#7c3aed', color: '#fff',
                                fontSize: 10, fontWeight: 700,
                                padding: '2px 6px', borderRadius: 999
                            }}>导出</span>
                            <div style={{
                                width: 44, height: 44, borderRadius: 12,
                                background: '#f5f3ff',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                marginBottom: 12
                            }}>
                                <BalanceListO style={{ fontSize: 24, color: '#7c3aed' }} />
                            </div>
                            <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>成绩导出</div>
                            <div style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                教育部模版·综合汇总·补测清册
                            </div>
                        </div>
                    </div>

                    {/* 模块标头：体测工具箱 */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 16px 10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 4, height: 16, background: '#056b4f', borderRadius: 2 }} />
                            <span style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>体测工具箱</span>
                            <span style={{ fontSize: 12, color: '#64748b' }}>审批、标准、档案与计算</span>
                        </div>
                        <span style={{ fontSize: 12, color: '#056b4f', fontWeight: 600, cursor: 'pointer' }} onClick={() => navigate('/physical-test/calculator')}>
                            计算器 ›
                        </span>
                    </div>

                    {/* 紧凑工具箱卡片 */}
                    <div style={{
                        margin: '0 16px 20px',
                        background: '#ffffff',
                        borderRadius: 16,
                        padding: '16px 10px',
                        boxShadow: 'var(--shadow-card)',
                        border: '1px solid var(--border-subtle)',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: 8,
                        textAlign: 'center'
                    }}>
                        <div onClick={() => navigate('/physical-test/exemption')} style={{ cursor: 'pointer' }}>
                            <div style={{
                                width: 44, height: 44, borderRadius: 12,
                                background: '#ecfdf5', margin: '0 auto 8px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <ShieldO style={{ fontSize: 22, color: '#00b578' }} />
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>免测申请</div>
                        </div>

                        <div onClick={() => navigate('/physical-test/standards')} style={{ cursor: 'pointer' }}>
                            <div style={{
                                width: 44, height: 44, borderRadius: 12,
                                background: '#fffbeb', margin: '0 auto 8px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <MedalO style={{ fontSize: 22, color: '#fa8c16' }} />
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>国家标准</div>
                        </div>

                        <div onClick={() => navigate('/physical-test/calculator')} style={{ cursor: 'pointer' }}>
                            <div style={{
                                width: 44, height: 44, borderRadius: 12,
                                background: '#f0fdf4', margin: '0 auto 8px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <Description style={{ fontSize: 22, color: '#056b4f' }} />
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>成绩计算</div>
                        </div>

                        <div onClick={() => navigate('/physical-test/query')} style={{ cursor: 'pointer' }}>
                            <div style={{
                                width: 44, height: 44, borderRadius: 12,
                                background: '#f5f3ff', margin: '0 auto 8px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <Search style={{ fontSize: 22, color: '#7c3aed' }} />
                            </div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>明细检索</div>
                        </div>
                    </div>

                    {/* 底部暖心保障标语 */}
                    <div style={{
                        margin: '0 16px',
                        background: '#f1f5f9',
                        borderRadius: 12,
                        padding: '12px 14px',
                        fontSize: 12,
                        color: '#64748b',
                        lineHeight: 1.6
                    }}>
                        <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
                            <span>🌟</span>
                            <span>星翼云智慧体育 · 为一线体育教师减负增效而设计！</span>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                            <span>🛡️</span>
                            <span>支持免网络离线极速录入，数据实时本地缓存防丢失！</span>
                        </div>
                    </div>
                </div>
            ) : (
                /* ========== 轨道 2：运动会与班级趣味赛 ========== */
                <div style={{ marginTop: 14 }}>
                    {/* 班级赛主推大卡 (对标 App 端 Gradient Card) */}
                    <div
                        onClick={() => navigate('/sports/class-match')}
                        style={{
                            margin: '0 16px 16px',
                            background: 'linear-gradient(135deg, #0891b2 0%, #115e59 100%)',
                            borderRadius: 18,
                            padding: '20px',
                            color: '#ffffff',
                            position: 'relative',
                            overflow: 'hidden',
                            boxShadow: '0 8px 24px rgba(13, 148, 136, 0.25)',
                            cursor: 'pointer'
                        }}
                    >
                        <div style={{ position: 'relative', zIndex: 1 }}>
                            <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                background: 'rgba(255,255,255,0.2)', padding: '3px 8px',
                                borderRadius: 999, fontSize: 11, fontWeight: 700, marginBottom: 8
                            }}>
                                <span>✨</span> 校园联赛
                            </div>
                            <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 4 }}>班级趣味对抗赛</div>
                            <div style={{ fontSize: 13, opacity: 0.9, marginBottom: 16 }}>全员参与 · 悦动校园 · 赛程积分实时看</div>
                            <div style={{
                                display: 'inline-block',
                                background: '#ffffff',
                                color: '#0891b2',
                                padding: '6px 14px',
                                borderRadius: 999,
                                fontSize: 12,
                                fontWeight: 700
                            }}>
                                进入赛事大厅 ›
                            </div>
                        </div>
                        {/* 装饰圆圈 */}
                        <div style={{
                            position: 'absolute', right: -20, bottom: -20,
                            width: 120, height: 120, borderRadius: '50%',
                            background: 'rgba(255,255,255,0.08)'
                        }} />
                    </div>

                    {/* 模块标头：运动会赛事枢纽 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '0 16px 10px' }}>
                        <div style={{ width: 4, height: 16, background: '#ff5e1e', borderRadius: 2 }} />
                        <span style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>运动会赛事体系</span>
                        <span style={{ fontSize: 12, color: '#64748b' }}>执裁、日程与实时榜单</span>
                    </div>

                    {/* 6格行动卡片 */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, 1fr)',
                        gap: 10,
                        padding: '0 16px'
                    }}>
                        {[
                            { icon: <BullhornO style={{ fontSize: 26, color: '#e11d48' }} />, text: '裁判工作台', path: '/sports/referee/login', bg: '#fff1f2' },
                            { icon: <CalendarO style={{ fontSize: 26, color: '#04523c' }} />, text: '比赛日程', path: '/sports/schedule', bg: '#f0fdf4' },
                            { icon: <Exchange style={{ fontSize: 26, color: '#d97706' }} />, text: '实时榜单', path: '/sports/rank', bg: '#fef3c7' },
                            { icon: <ManagerO style={{ fontSize: 26, color: '#059669' }} />, text: '运动员查询', path: '/sports/athletes', bg: '#ecfdf5' },
                            { icon: <BalanceListO style={{ fontSize: 26, color: '#059669' }} />, text: '报表中心', path: '/sports/reports', bg: '#f0fdf4' },
                            { icon: <Play style={{ fontSize: 26, color: '#db2777' }} />, text: '赛事直播', path: '/sports/live', bg: '#fdf2f8' },
                        ].map((item, idx) => (
                            <div
                                key={idx}
                                onClick={() => navigate(item.path)}
                                style={{
                                    background: '#ffffff',
                                    borderRadius: 16,
                                    padding: '16px 8px',
                                    textAlign: 'center',
                                    border: '1px solid #f1f5f9',
                                    boxShadow: 'var(--shadow-card)',
                                    cursor: 'pointer'
                                }}
                            >
                                <div style={{
                                    width: 48, height: 48, borderRadius: 14,
                                    background: item.bg, margin: '0 auto 10px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    {item.icon}
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{item.text}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}
